import classnames from 'classnames';
import { Modal as ModalModule, Print, UI, Utils } from 'common';
import isArray from 'lodash/isArray';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React from 'react';
import ReactToPrint from 'react-to-print';
import HistoryCommunication from '../utils/HistoryCommunication';

const { Modal, ModalButtonActionType } = ModalModule;
const {
	WrappedInputNumberWithLabel,
	WrappedTooltip,
	WrappedButton,
	WrappedButtonType,
} = UI;
const { Session, withLayoutAndModalContexts } = Utils;
const { PrintModal, PdfPreviewModal } = Print;

const ViewType = {
	SelectContainers: 'SelectContainers',
	PrintLabel: 'PrintLabel',
	ReprintLabel: 'ReprintLabel',
};

class CreateContainerLabelModal extends React.Component {
	constructor(props) {
		super(props);

		const { data = {} } = props;
		const { prints = [], isReprint = false } = data;
		const totalPrintsWeight = this.getTotalWeight(prints);

		this.minContainerLabels = Math.ceil(totalPrintsWeight / 70); // minimum value for number of container labels
		this.maxContainerLabels = prints.length; // max value for number of container labels

		this.state = {
			numberOfContainerLabels: this.minContainerLabels,
			isValid: null, // true if "numberOfContainerLabels" is a valid number
			isReprint, // true if user is reprinting the container labels
			errorMessage: '',
			viewType: isReprint ? ViewType.PrintLabel : ViewType.SelectContainers,
			isContainerLabelButtonDisabeld: false,
			imageUrls: [],
		};

		this.containerLabelImgRef = null;
		this.reactToPrintButton = React.createRef();
	}

	componentDidMount() {
		const { isReprint } = this.state;

		if (isReprint) {
			this.onReprintLabelButtonClick();
		}
	}

	/**
	 * Build payload to create container labels.
	 * @returns {object}
	 */
	buildContainerLabelPayload = () => {
		const { data = {} } = this.props;
		const { prints = [] } = data;
		const { numberOfContainerLabels } = this.state;
		const { fromAddress = {} } = prints[0].printDetails || {};

		return {
			label_ids: prints.map(({ printDetails = {} }) => printDetails.labelId),
			label_options: {
				label_format: 'png',
			},
			number_of_container_labels: numberOfContainerLabels,
			from_address: {
				company_name: fromAddress.companyName,
				name: fromAddress.name,
				address_line1: fromAddress.addressLine1,
				address_line2: fromAddress.addressLine2,
				address_line3: fromAddress.addressLine3,
				city: fromAddress.city,
				state_province: fromAddress.stateProvince,
				postal_code: `${fromAddress.postalCode}${
					fromAddress?.postalCodeAddon ? `-${fromAddress.postalCodeAddon}` : ''
				}`,
			},
		};
	};

	/**
	 * Check whether "numberOfContainerLabels" is a valid number
	 * - value must be between "minContainerLabels" and "maxContainerLabels" inclusively.
	 * @returns {boolean}
	 */
	isNumberOfContainerLabelValid = () => {
		const { numberOfContainerLabels } = this.state;
		const validation = {
			isValid: true,
			errorMessage: '',
		};

		if (numberOfContainerLabels < this.minContainerLabels) {
			validation.isValid = false;
			validation.errorMessage =
				'Each container has a maximum weight of 70 lbs. You will need more containers to meet this requirement.';
		} else if (numberOfContainerLabels > this.maxContainerLabels) {
			validation.isValid = false;
			validation.errorMessage =
				'You cannot print more container labels than the number of eligible packages.';
		}

		this.setState(validation);

		return validation.isValid;
	};

	/**
	 * Return modal header text based on "viewType" and "isReprint"
	 * @returns {string}
	 */
	getHeader = () => {
		const { viewType } = this.state;

		if (viewType === ViewType.SelectContainers) {
			return 'Container Label';
		} else if (viewType === ViewType.ReprintLabel) {
			return 'Your Container Label';
		}
	};

	/**
	 * Return total weight of eligible container label prints"
	 * @returns {number}
	 */
	getTotalWeight = prints =>
		prints.reduce((_totalWeight, print) => {
			const { printDetails } = print || {};
			const { weightPounds = 0, weightOunces = 0 } = printDetails;

			// 1 ounce equals 0.0625 pounds
			return +_totalWeight + weightPounds + weightOunces * 0.0625;
		}, 0);

	onCancelButtonClick = () => {
		const { modalId, modalContext } = this.props;

		modalContext.closeModal(modalId);
	};

	onPrintPreviewButtonClick = () => {
		if (this.isNumberOfContainerLabelValid()) {
			return this.createContainerLabel();
		}
	};

	createContainerLabel = async () => {
		const { modalContext, data = {} } = this.props;
		const accountInfo = Session.getAccountInfo();
		const { userId } = accountInfo;

		this.setState({
			isContainerLabelButtonDisabeld: true,
		});
		// For reprint, system will not make the call to get the label URL(s).
		// Instead, the label id(s) will come from localStorage.
		const { enableReprintLastLabelMenuCallback } = data;
		const payload = this.buildContainerLabelPayload();
		const response = await HistoryCommunication.createContainerLabel(payload);

		if (!HistoryCommunication.isSuccessful(response)) {
			const { errorCode, errorMessage } = this.parseErrorResponse(response);

			modalContext.closeAll(() => {
				modalContext.showError({
					data: {
						// display the error code
						code: isNumber(errorCode) && !isNaN(errorCode) ? errorCode : null,
						title: !isNumber(errorCode) ? 'Error' : null,
						description: `<p>${errorMessage}</p>`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			});

			return null;
		}

		// stores label URL(s) in the localStorage for reprint
		localStorage.setItem(
			`reprint_container_label_ids_${userId}`,
			payload.label_ids
		);

		if (enableReprintLastLabelMenuCallback) {
			enableReprintLastLabelMenuCallback();
		}

		this.setState({
			isContainerLabelButtonDisabeld: false,
		});

		this.renderPrintLabelView(response.data);
	};

	parseErrorResponse = response =>
		isObject(response.error)
			? response.error
			: {
					errorMessage: response.ErrorDescription,
				};

	onReprintLabelButtonClick = async () => {
		const { modalContext } = this.props;
		const accountInfo = Session.getAccountInfo();
		const { userId } = accountInfo;
		const containerLabelIds = localStorage.getItem(
			`reprint_container_label_ids_${userId}`
		);
		const labelIds = containerLabelIds.split(',') || [containerLabelIds];
		const labels = [];

		labelIds.forEach(async labelId => {
			const response =
				await HistoryCommunication.reprintContainerLabel(labelId);
			labels.push(response?.data?.labels[0]);

			if (labels.length === labelIds.length) {
				if (!HistoryCommunication.isSuccessful(response)) {
					const { errorCode, errorMessage } = this.parseErrorResponse(response);

					modalContext.closeAll(() => {
						modalContext.showError({
							data: {
								// display the error code
								code:
									isNumber(errorCode) && !isNaN(errorCode) ? errorCode : null,
								title: !isNumber(errorCode) ? 'Error' : null,
								description: `<p>${errorMessage}</p>`,
								primaryButtonText: 'OK',
								handleType: 'custom',
							},
						});
					});

					return null;
				}
				response.data.labels = labels;
				this.renderPrintLabelView(response.data);
			}
		});
	};

	onChange = ({ target }) => {
		const { viewType } = this.state;
		const { name, value } = target;

		this.setState(
			{
				[name]: value,
			},
			() => {
				if (viewType === ViewType.SelectContainers) {
					this.isNumberOfContainerLabelValid();
				}
			}
		);
	};

	// render "Container Label" modal content
	renderSelectContainersView = () => {
		const { data = {} } = this.props;
		const {
			numberOfContainerLabels,
			isValid,
			errorMessage,
			isContainerLabelButtonDisabeld,
		} = this.state;
		const { prints = [] } = data;

		const shouldDisplayWarningIcon =
			numberOfContainerLabels > this.minContainerLabels &&
			numberOfContainerLabels <= this.maxContainerLabels;

		return (
			<>
				<p>
					You have {prints.length} packages that should be sent to the Global
					Advantage Program processing facility.
				</p>
				<p>
					Place each package inside one or more large containers (70lb max).
					Specify the number of containers below to print a free pre-addressed
					label for each container. Each of these labels can then be included in
					a SCAN form.
				</p>
				<div className="input-container">
					<WrappedInputNumberWithLabel
						isInputInvalid={isValid === false}
						label="Number of Containers"
						errorMessage={errorMessage}
						uiElementConfig={{
							name: 'numberOfContainerLabels',
							value: numberOfContainerLabels,
							showButtons: true,
							onChange: this.onChange,
							min: 0,
							dataqa: 'number-of-container-labels',
						}}
					/>
					{shouldDisplayWarningIcon && (
						<>
							<span
								className="warning-icon display-warning-icon-container-labels"
								data-qa="invalid-warning-icon"
								{...{ dataqa: 'invalid-warning-icon' }}
							/>
							<WrappedTooltip
								target="display-warning-icon-container-labels"
								position="right"
								tooltip="Please try to use as few Container Labels as possible"
							/>
						</>
					)}
				</div>
				<div className="footer">
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						onClick={this.onPrintPreviewButtonClick}
						label="Print Label"
						data-qa="modal-primary"
						dataqa="create-container-label-print"
						disabled={isContainerLabelButtonDisabeld}
					/>
					<WrappedButton
						buttonType={WrappedButtonType.Secondary}
						onClick={this.onCancelButtonClick}
						label="Cancel"
						data-qa="modal-secondary"
						dataqa="create-container-label-cancel"
					/>
				</div>
			</>
		);
	};

	/**
	 * Checks if container label URLs are GPX format (PDF only)
	 * GPX URLs have the format: .../gpx.ashx?StampsTxId=...
	 * @param {Array<string>} labels - Array of label URLs
	 * @returns {boolean}
	 */
	isGpxFormat = labels => {
		return (
			labels.length > 0 && labels.every(url => url && url.includes('gpx.ashx'))
		);
	};

	// render "Print label" modal content
	renderPrintLabelView = response => {
		const { modalContext } = this.props;
		const { isReprint } = this.state;
		const labels = response.labels.map(({ href }) => href).filter(url => url); // Filter out null/undefined URLs

		this.setState({
			viewType: ViewType.PrintLabel,
		});

		// GPX format URLs only support PDF printing - show PDF preview directly
		if (this.isGpxFormat(labels)) {
			modalContext.showModal({
				component: PdfPreviewModal,
				data: {
					pdfUrls: labels,
				},
				closeCallback: () => {
					this.onAfterPrint();
				},
			});

			return;
		}

		// PNG format URLs - continue with existing PrintModal flow
		modalContext.showModal({
			component: PrintModal,
			data: {
				header: isReprint
					? 'Reprint'
					: `You have ${response.number_of_container_labels} label ready to print`,
				labels,
				carrier: response.carrier,
				totalCost: response.totalCost,
			},

			closeCallback: (closeType, modalData) => {
				// confirm printing
				if (closeType === ModalButtonActionType.Confirm) {
					const { sheet, startPosition } = modalData;

					this.createMediaGen(sheet, startPosition, labels);
				}
			},
		});
	};

	createMediaGen = async (sheet, startPosition, labelUrls) => {
		// make a request to mediagen
		const mediaGenResponse = await HistoryCommunication.createMediaGen({
			labelFormat: 'png',
			labelUrls,
			sheet,
			startPos: startPosition,
		});

		const { data: mediaData } = mediaGenResponse;
		const imageUrls = isArray(mediaData)
			? mediaData.map(({ imageUrl }) => imageUrl)
			: [];

		this.setState({
			imageUrls,
		});
	};

	onAfterPrint = () => {
		const {
			data: { onRefreshGridAPI },
		} = this.props;

		if (onRefreshGridAPI) {
			onRefreshGridAPI();
		}

		this.setState({
			imageUrls: [],
			viewType: ViewType.ReprintLabel,
		});

		Session.loadContainerLabelCount();
	};

	// render "Your Container Label" modal content
	renderReprintLabelView = () => (
		<>
			<p>The print will start automatically.</p>
			<p>
				Your Container Labels can then be included in a SCAN Form and scheduled
				for pickup.
			</p>
			<div className="footer">
				<WrappedButton
					buttonType={WrappedButtonType.Primary}
					onClick={this.onReprintLabelButtonClick}
					label="Reprint"
					data-qa="modal-primary"
					dataqa="create-container-label-reprint"
				/>
				<WrappedButton
					buttonType={WrappedButtonType.Secondary}
					onClick={this.onCancelButtonClick}
					label="Close"
					data-qa="modal-secondary"
					dataqa="create-container-label-close"
				/>
			</div>
		</>
	);

	onLoad = () => {
		// trigger print after the image is loaded
		this.reactToPrintButton.current.click();
	};

	render() {
		const { modalId } = this.props;
		const { viewType, imageUrls } = this.state;

		return (
			<div>
				{viewType !== ViewType.PrintLabel && (
					<Modal
						modalId={modalId}
						className={classnames(
							'container-label create-container-label-modal',
							{
								'reprint-container-label-modal':
									viewType === ViewType.ReprintLabel,
							}
						)}
						header={this.getHeader()}
						hideFooter
					>
						{viewType === ViewType.SelectContainers &&
							this.renderSelectContainersView()}
						{viewType === ViewType.ReprintLabel &&
							this.renderReprintLabelView()}
					</Modal>
				)}
				{imageUrls.length > 0 && (
					<>
						<ReactToPrint
							trigger={() => (
								<button
									id="printTrigger"
									type="button"
									style={{ display: 'none' }}
									ref={this.reactToPrintButton}
								>
									Print
								</button>
							)}
							content={() => this.containerLabelImgRef}
							onAfterPrint={this.onAfterPrint}
						/>
						<div style={{ display: 'none' }}>
							<div ref={el => (this.containerLabelImgRef = el)}>
								{imageUrls.map((url, index) => (
									<img
										key={index}
										alt="Label"
										style={{ width: '100%', height: '100%' }}
										src={url}
										onLoad={() => {
											if (index === imageUrls.length - 1) {
												this.onLoad();
											}
										}}
									/>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		);
	}
}

CreateContainerLabelModal.propTypes = {
	data: PropTypes.shape({
		// eligible prints to create container labels
		prints: PropTypes.array,

		// true to show "PrintLabel" view
		isReprint: PropTypes.bool,

		// callback to enable reprint menu in Toolbar
		enableReprintLastLabelMenuCallback: PropTypes.func,
	}),
};

export default withLayoutAndModalContexts(CreateContainerLabelModal);
